// Pieces shared by the website's analysis tab and the scan page: the score with its parts and key
// figures, the comparison with the previous scan, page tiles with their traffic-light colour, and the
// issues with their follow-up.
import { ArrowDownIcon, ArrowUpIcon, CheckCircledIcon, InfoCircledIcon } from '@radix-ui/react-icons';
import { Link } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { captureUrl, type Scan, type ScanComparison, type ScannedPage } from '../api';
import { type PageFamily, primaryLanguage } from '../page-families';
import type { PageProblems } from '../results-logic';
import { WebsiteLink } from '../ui';
import { pathOf, publicPage } from '../util';
import { ScoreLegend, ScoreRing, SeverityChips, ToneIcon, useDrawn, worstTone } from '../viz';

export function useDate() {
  const { i18n } = useTranslation();
  return (iso: string, style: 'long' | 'short' = 'long') =>
    new Intl.DateTimeFormat(i18n.language, {
      dateStyle: style === 'long' ? 'long' : 'medium',
      ...(style === 'long' ? { timeStyle: 'short' } : {}),
    }).format(new Date(iso));
}

/** SEO and quality: one number from 0 to 100 each, as a small ring. */
export function ModuleRing({ score, label }: { score: number | null; label: string }) {
  const { t } = useTranslation();
  const drawn = useDrawn();
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  return (
    <div className="module-ring">
      <div
        className="score-ring score-ring-sm"
        role="img"
        aria-label={
          score === null ? t('viz.module.none', { label }) : t('viz.module.label', { label, score })
        }
      >
        <svg viewBox="0 0 120 120" aria-hidden="true">
          <circle className="ring-track" r={radius} cx="60" cy="60" />
          {score !== null && (
            <circle
              className="ring-segment ring-module"
              r={radius}
              cx="60"
              cy="60"
              transform="rotate(-90 60 60)"
              strokeDasharray={`${drawn ? (score / 100) * circumference : 0} ${circumference}`}
            />
          )}
        </svg>
        <span className="score-ring-value" aria-hidden="true">
          {score ?? '—'}
        </span>
      </div>
      <span className="module-ring-label" aria-hidden="true">
        {label}
      </span>
    </div>
  );
}

/** Against the previous scan: how the score moved, and the issues that appeared or are gone. */
export function ComparisonChips({
  comparison,
  score,
}: {
  comparison: ScanComparison | null;
  score: number | null;
}) {
  const { t } = useTranslation();
  const date = useDate();
  if (!comparison) return null;
  const before = comparison.previous.score;
  const delta = score !== null && before !== null ? score - before : null;
  return (
    <div className="comparison">
      <p className="comparison-title">
        {t('compare.title', { date: date(comparison.previous.createdAt, 'short') })}
      </p>
      <ul className="comparison-chips">
        {delta !== null && (
          <li data-trend={delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat'}>
            {delta > 0 ? (
              <ArrowUpIcon aria-hidden="true" />
            ) : delta < 0 ? (
              <ArrowDownIcon aria-hidden="true" />
            ) : null}
            {delta === 0
              ? t('compare.same')
              : t('compare.points', { count: delta, signed: `${delta > 0 ? '+' : ''}${delta}` })}
          </li>
        )}
        <li data-trend={comparison.resolvedIssues > 0 ? 'up' : 'flat'}>
          <CheckCircledIcon aria-hidden="true" />
          {t('compare.resolved', { count: comparison.resolvedIssues })}
        </li>
        <li data-trend={comparison.newIssues > 0 ? 'down' : 'flat'}>
          {t('compare.new', { count: comparison.newIssues })}
        </li>
      </ul>
    </div>
  );
}

/** The score with its parts and the modules: secondary information about a finished scan, with the notes about pages the website limited. */
export function ScoreHero({
  scan,
  comparison,
  engineScore,
  discarded = 0,
  children,
}: {
  scan: Scan;
  comparison?: ScanComparison | null;
  engineScore?: number | null;
  /** Elements the team discarded with a justification: they are not in this score. */
  discarded?: number;
  children?: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const scores = scan.scores;
  const score = scores?.accesibilidad.score ?? null;
  // Pages limited by the website's rate limit are also counted as blocked: show them on their own line.
  const limitedCount = scan.stats?.pagesLimited ?? 0;
  const blockedCount = Math.max(0, (scan.stats?.pagesBlocked ?? 0) - limitedCount);
  const partialCount = scan.stats?.pagesPartial ?? 0;
  return (
    <div className="score-hero">
      <h4 id="estimacion-automatica" className="score-hero-heading">
        {t('results.score.title')}
        {score !== null && (
          <span className="score-hero-heading-value">{t('results.score.value', { score })}</span>
        )}
      </h4>
      <div className="score-hero-ring">
        <ScoreRing scores={scores?.accesibilidad} size="lg" />
        <div className="score-hero-text">
          <p className="score-hero-band">
            {score === null
              ? t('scans.noScore')
              : t(`scans.bands.${scores?.accesibilidad.band ?? 'sin-datos'}`)}
          </p>
          <p className="score-hero-note">
            <InfoCircledIcon aria-hidden="true" />
            {t('scans.scoreNote')}
          </p>
          <ScoreLegend scores={scores?.accesibilidad} />
          {discarded > 0 && (
            <p className="text-sm">
              {t('scan.discardedNote', { count: discarded })}{' '}
              <WebsiteLink href={`${publicPage(i18n.language, 'methodology')}#decisiones`}>
                {t('scan.discardedMethod')}
              </WebsiteLink>
            </p>
          )}
          {engineScore != null && engineScore !== score && (
            <p className="text-sm">{t('scan.engineScore', { score: engineScore })}</p>
          )}
        </div>
      </div>
      <div className="score-hero-side">
        <div className="module-rings">
          <ModuleRing score={scores?.seo.score ?? null} label={t('scans.modules.seo')} />
          <ModuleRing score={scores?.calidad.score ?? null} label={t('scans.modules.quality')} />
        </div>
        {blockedCount > 0 && <p className="text-sm">{t('scans.blocked', { count: blockedCount })}</p>}
        {limitedCount > 0 && <p className="text-sm">{t('scans.limited', { count: limitedCount })}</p>}
        {partialCount > 0 && <p className="text-sm">{t('scans.partial', { count: partialCount })}</p>}
        {comparison !== undefined && <ComparisonChips comparison={comparison} score={score} />}
        {children}
      </div>
    </div>
  );
}

const pageLinkSearch = (scanId: string, pageKey: string, problemKey?: string | null) => ({
  scan: scanId,
  p: pageKey,
  ...(problemKey ? { k: problemKey } : {}),
});

/** A page as a tile: its screenshot framed in the colour of its worst failure, and its counts. */
export function PageTile({
  scanId,
  siteId,
  page,
  problemKey,
  current = false,
}: {
  scanId: string;
  siteId: string;
  page: ScannedPage;
  /** Opens the page with this problem selected (`?k=`). */
  problemKey?: string | null;
  current?: boolean;
}) {
  const { t } = useTranslation();
  const tone = page.blocked || page.error ? 'none' : worstTone(page.counts);
  const name = page.title || pathOf(page.finalUrl);
  return (
    <Link
      to="/sites/$siteId/paginas"
      params={{ siteId }}
      search={pageLinkSearch(scanId, page.key, problemKey)}
      className="page-tile"
      data-tone={tone}
      aria-current={current ? 'page' : undefined}
    >
      <span className="tile-shot">
        {page.captures.miniatura ? (
          <img src={captureUrl(scanId, page.key, 'miniatura')} alt="" loading="lazy" decoding="async" />
        ) : (
          <span className="tile-shot-empty" aria-hidden="true" />
        )}
      </span>
      <span className="tile-caption">
        <span className="tile-title">{name}</span>
        <span className="tile-path">{pathOf(page.finalUrl)}</span>
        {page.blocked ? (
          <span className="tone-chip" data-tone="none">
            {t('scan.pageBlockedShort')}
          </span>
        ) : page.error ? (
          <span className="tone-chip" data-tone="none">
            {t('scan.pageErrorShort')}
          </span>
        ) : (
          <SeverityChips counts={page.counts} />
        )}
      </span>
    </Link>
  );
}

/** The address without the scheme, which says the same on every card of one website. */
const shortUrl = (url: string) => url.replace(/^https?:\/\//, '');

/**
 * What a page of the analysis says about itself, the same in its card and in the list beside the capture: its
 * tone (the colour of its worst priority, green with none detected, grey when it could not be analysed) and
 * the words for it («3 problemas», «Sin problemas» / «Sin problemas detectados», «Bloqueada»).
 */
export function usePageStatus(page: ScannedPage, found?: PageProblems, short = false) {
  const { t } = useTranslation();
  const unanalysed = Boolean(page.blocked || page.error);
  const problems = found?.problems ?? 0;
  const top = found?.byPriority.alta ? 'critica' : found?.byPriority.media ? 'moderada' : 'leve';
  // A page whose only problems are shared components another page looks after is not a page without problems.
  const onlyGlobal = Boolean(found && problems === 0 && found.delegated > 0);
  const delegatedTone =
    found?.delegatedTop === 'alta' ? 'critica' : found?.delegatedTop === 'media' ? 'moderada' : 'leve';
  const tone = unanalysed
    ? 'none'
    : found
      ? problems > 0
        ? top
        : onlyGlobal
          ? delegatedTone
          : 'ok'
      : 'none';
  const status = unanalysed
    ? page.blocked
      ? t('scan.pageBlockedShort')
      : t('scan.pageErrorShort')
    : found
      ? problems > 0
        ? t('results.pageCard.problems', { count: problems })
        : onlyGlobal
          ? t('results.pageCard.onlyGlobal', { count: found?.delegated ?? 0 })
          : t(short ? 'results.pageCard.noneShort' : 'results.pageCard.none')
      : null;
  return { tone, status, unanalysed } as const;
}

/** The thumbnail of a page; the same drawing as for a page without one when the file cannot be loaded. */
function Thumbnail({ scanId, page }: { scanId: string; page: ScannedPage }) {
  const [failed, setFailed] = useState(false);
  return page.captures.miniatura && !failed ? (
    <img
      src={captureUrl(scanId, page.key, 'miniatura')}
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  ) : (
    <span className="tile-shot-empty" aria-hidden="true" />
  );
}

/**
 * A page of the analysis as a vertical card: its screenshot on top, its name and address and how many
 * problems it has (counted as the summary counts them: one per rule). The whole card is the link to the
 * page's problems (the name is the link, stretched over the card), and the colour of its top edge tells the
 * pages with problems from the ones with none detected; the chip says it in words too.
 */
export function PageCard({
  scanId,
  siteId,
  page,
  found,
  problemKey,
  current = false,
  languages,
  statusText,
  footer,
  shortStatus = false,
}: {
  scanId: string;
  siteId: string;
  page: ScannedPage;
  /** What the page has, in the units of the summary; absent while the issues are not known. */
  found?: PageProblems;
  problemKey?: string | null;
  /** The words of the chip when the list has its own («2 problemas pendientes»); by default, the card's. */
  statusText?: (status: string | null, found?: PageProblems) => string | null;
  /** More lines under the chip (the list of pages: corrections verified, date of the latest analysis). */
  footer?: ReactNode;
  /** The language versions of the page, as a selector over the card (see PageFamilyCard). */
  languages?: ReactNode;
  /** The page being looked at (in the list beside its capture): marked beyond colour, and said to assistive technology. */
  current?: boolean;
  /** Compact chip («Sin problemas») for dense grids such as the Análisis tab. */
  shortStatus?: boolean;
}) {
  const { t } = useTranslation();
  const name = page.title || pathOf(page.finalUrl);
  const { tone, status: defaultStatus, unanalysed } = usePageStatus(page, found, shortStatus);
  const status = statusText ? statusText(defaultStatus, found) : defaultStatus;
  return (
    <article className="page-card" data-tone={tone} data-current={current || undefined}>
      <span className="page-card-shot">
        <Thumbnail scanId={scanId} page={page} />
      </span>
      <div className="page-card-body">
        <p className="page-card-title">
          <Link
            to="/sites/$siteId/paginas"
            params={{ siteId }}
            search={pageLinkSearch(scanId, page.key, problemKey)}
            className="page-card-link"
            aria-current={current ? 'page' : undefined}
          >
            {name}
            {/* What the chip says, for whoever hears the link: the chip itself is only drawn. */}
            {status && <span className="sr-only">: {status}</span>}
          </Link>
        </p>
        <p className="page-card-url" title={page.finalUrl}>
          {shortUrl(page.finalUrl)}
        </p>
        {status && (
          <span className="tone-chip" data-tone={tone} aria-hidden="true">
            {!unanalysed && <ToneIcon tone={tone} />}
            {status}
            {found && found.global > 0 && ` (${t('results.pageSummary.global', { count: found.global })})`}
          </span>
        )}
        {footer}
        {languages}
      </div>
    </article>
  );
}

/**
 * A page that exists in several languages as one card: the main version shows, and a selector over the card changes it
 * to the other versions (each with its own screenshot, address and problems). The card links to the version shown.
 * `current` is the page being looked at: when it is one of the versions, that is the one shown.
 */
export function PageFamilyCard({
  scanId,
  scanOf,
  siteId,
  family,
  found,
  currentKey,
  onPick,
  statusText,
  problemKeyOf,
  footer,
  shortStatus = false,
}: {
  /** The analysis the pages come from; or, when each page has its own latest one, `scanOf`. */
  scanId?: string;
  scanOf?: (key: string) => string;
  siteId: string;
  family: PageFamily<ScannedPage>;
  found: (key: string) => PageProblems | undefined;
  currentKey?: string;
  /** Choosing a version opens it (the list beside a page) instead of only showing it on the card. */
  onPick?: (key: string) => void;
  statusText?: (status: string | null, found?: PageProblems) => string | null;
  problemKeyOf?: (key: string) => string | null | undefined;
  /** More lines under the chip, for the version shown. */
  footer?: (page: ScannedPage) => ReactNode;
  /** Compact chip («Sin problemas») for dense grids such as the Análisis tab. */
  shortStatus?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [picked, setPicked] = useState<string | null>(null);
  const inFamily = family.members.some((member) => member.key === currentKey);
  const shown =
    (inFamily ? family.members.find((member) => member.key === currentKey) : undefined) ??
    family.members.find((member) => member.key === picked) ??
    family.members[0]!;
  const names = (() => {
    try {
      return new Intl.DisplayNames([i18n.language], { type: 'language' });
    } catch {
      return null;
    }
  })();
  const codeOf = (page: ScannedPage) => (primaryLanguage(page.lang) ?? '').toUpperCase();
  const languages =
    family.members.length > 1 ? (
      <fieldset className="page-card-langs">
        <legend className="sr-only">{t('results.pageCard.languages')}</legend>
        {family.members.map((member) => {
          const code = primaryLanguage(member.lang) ?? '';
          return (
            <button
              key={member.key}
              type="button"
              aria-pressed={member.key === shown.key}
              aria-label={t('results.pageCard.showLanguage', { language: names?.of(code) ?? code })}
              onClick={() => (onPick && inFamily ? onPick(member.key) : setPicked(member.key))}
            >
              {codeOf(member)}
            </button>
          );
        })}
      </fieldset>
    ) : undefined;
  return (
    <PageCard
      scanId={scanOf ? scanOf(shown.key) : (scanId ?? '')}
      problemKey={problemKeyOf?.(shown.key)}
      siteId={siteId}
      page={shown}
      found={found(shown.key)}
      current={inFamily}
      languages={languages}
      statusText={statusText}
      footer={footer?.(shown)}
      shortStatus={shortStatus}
    />
  );
}
